import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";

import {
  AuthWrapper,
  FormWrapper,
  ContentLogin,
  ImageWrapper,
  Form,
  ConfirmWrapp,
  LinkWrap,
  EmptyColumn,
  ErrorMessage,
} from "../../style/Auth";

import logo from "../../images/ic_logo.svg";
import Button from "../../components/button/index";
import PlayerLoader from "../../components/PlayerLoader";
import { useLexicon } from "../../hooks/useLexicon";

import {
  createSetupIntent,
  startSelfServeTrial,
  fetchSubscriptionPlans,
} from "./api";

// Stripe amounts are in minor units (6900 = €69.00). Format from the
// /subscription/plans payload so we never hard-code prices on the card step.
const interp = (tmpl, nodes) => {
  const parts = String(tmpl).split(/\{[^}]+\}/);
  return parts.flatMap((p, i) => (i < nodes.length ? [p, nodes[i]] : [p]));
};

const CURRENCY_SYMBOL = { eur: "€", usd: "$", gbp: "£" };

const formatPlanPrice = (plan) => {
  const symbol = CURRENCY_SYMBOL[plan?.currency?.toLowerCase()] ?? "";
  const major = (plan?.amount ?? 0) / 100;
  const amount = Number.isInteger(major) ? String(major) : major.toFixed(2);
  return `${symbol}${amount}/${plan?.interval ?? ""}`;
};

// /player/setup-card — Stripe Elements bound to a fresh SetupIntent.
// On confirm we hand the SetupIntent id to the API which validates it
// server-side and starts the 2h trial. PRD §11.4. Same auth chrome as
// /login + /player/register.

const stripeAppearance = {
  theme: "night",
  variables: {
    colorPrimary: "#C8FF00",
    colorBackground: "#1A1F28",
    colorText: "#FFFFFF",
    colorDanger: "#F20A46",
    fontFamily: "-apple-system, Segoe UI, Roboto, sans-serif",
    borderRadius: "8px",
  },
};

const SetupCardPage = () => {
  const l = useLexicon();
  const [bootstrap, setBootstrap] = useState({
    loading: true,
    error: null,
    clientSecret: null,
    setupIntentId: null,
    publishableKey: null,
  });

  // Configured plan prices for the reassurance copy. No plan is chosen at the
  // card step yet, so we show both (e.g. "€69/month or €690/year"). Loaded
  // best-effort — a failure must not block the card form.
  const [priceText, setPriceText] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchSubscriptionPlans();
        const plans = res?.data?.plans ?? [];
        const text = plans
          .filter((p) => p?.amount != null)
          .map(formatPlanPrice)
          .join(" or ");
        if (!cancelled && text) setPriceText(text);
      } catch {
        // Fall back to a price-free phrasing below.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await createSetupIntent();
        const { client_secret, setup_intent_id, publishable_key } = res.data ?? {};
        if (!client_secret || !publishable_key) throw new Error("Missing client_secret");
        if (!cancelled) {
          setBootstrap({
            loading: false,
            error: null,
            clientSecret: client_secret,
            setupIntentId: setup_intent_id,
            publishableKey: publishable_key,
          });
        }
      } catch (err) {
        if (cancelled) return;
        const code = err?.response?.data?.code;
        const message =
          code === "EMAIL_NOT_VERIFIED"
            ? l(1768)
            : err?.response?.data?.error || l(1769);
        setBootstrap({
          loading: false,
          error: message,
          clientSecret: null,
          setupIntentId: null,
          publishableKey: null,
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const stripePromise = useMemo(
    () => (bootstrap.publishableKey ? loadStripe(bootstrap.publishableKey) : null),
    [bootstrap.publishableKey]
  );

  return (
    <AuthWrapper>
      <FormWrapper>
        <ContentLogin>
          <EmptyColumn />
          <Form as="div">
            <a href="/">
              <img className="logo" src={logo} alt="Logo" />
            </a>
            <h1>{l(1770)}</h1>
            <p>{l(1771)}</p>
            <Reassure>
              <ReassureItem>
                <Check>✓</Check>
                <span>
                  <strong>{l(1772)}</strong> {l(1773)}
                </span>
              </ReassureItem>
              <ReassureItem>
                <Check>✓</Check>
                <span>
                  {priceText
                    ? interp(l(1774), [<strong key="p">{priceText}</strong>])
                    : l(1775)}
                </span>
              </ReassureItem>
              <ReassureItem>
                <Check>✓</Check>
                <span>
                  {interp(l(1776), [<strong key="b">{l(1777)}</strong>])}
                </span>
              </ReassureItem>
            </Reassure>

            {bootstrap.loading && <PlayerLoader label={l(1778)} />}

            {bootstrap.error && <ErrorMessage>{bootstrap.error}</ErrorMessage>}

            {!bootstrap.loading &&
              !bootstrap.error &&
              bootstrap.clientSecret &&
              stripePromise && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: bootstrap.clientSecret,
                    appearance: stripeAppearance,
                  }}
                >
                  <CardForm setupIntentId={bootstrap.setupIntentId} />
                </Elements>
              )}
          </Form>
          <LinkWrap>
            <h3>
              <Disclaimer>
                {interp(l(1779), [
                  <a key="t" href="https://smrtstats.com/terms" target="_blank" rel="noreferrer">{l(1743)}</a>,
                  <a key="p" href="https://smrtstats.com/privacy" target="_blank" rel="noreferrer">{l(1744)}</a>,
                ])}
              </Disclaimer>
            </h3>
          </LinkWrap>
        </ContentLogin>
      </FormWrapper>
      <ImageWrapper />
    </AuthWrapper>
  );
};

export default SetupCardPage;

const CardForm = ({ setupIntentId }) => {
  const l = useLexicon();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const submittedRef = useRef(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submittedRef.current) return;
    submittedRef.current = true;
    setSubmitting(true);
    setError(null);

    try {
      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: "if_required",
      });
      if (confirmError) {
        throw confirmError;
      }
      const intentIdToSubmit = setupIntent?.id || setupIntentId;
      await startSelfServeTrial(intentIdToSubmit);
      // Self-serve onboarding's last step: pick a platform_api_player.
      // The picker redirects already-mapped users straight to the
      // dashboard, so re-runs (e.g. trial reset, retry) are safe.
      navigate("/player/onboarding/pick-player", { replace: true });
    } catch (err) {
      submittedRef.current = false;
      setError(
        err?.message || err?.response?.data?.error || l(1782)
      );
      setSubmitting(false);
    }
  };

  return (
    <CardFormWrap onSubmit={onSubmit}>
      {/* Disable the Stripe Link wallet. Without this, Stripe injects a
          floating "easel" iframe (visible "stripe >" badge) that persists
          past unmount on some flows and covers the bottom-nav icons in the
          player PWA. Client-reported across browsers post-onboarding. We
          don't want Stripe Link in v1 anyway — card only. */}
      <PaymentElement
        options={{
          layout: { type: "tabs", defaultCollapsed: false },
          wallets: { applePay: "never", googlePay: "never" },
        }}
      />

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <ConfirmWrapp>
        <Button
          title={submitting ? l(1780) : l(1781)}
          size="full"
          sizeResponse="full"
          type="submit"
          disabled={!stripe || submitting}
        />
      </ConfirmWrapp>
    </CardFormWrap>
  );
};

const CardFormWrap = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 4px;
`;

// Prominent "no charge now" reassurance at the card step — the highest
// friction point of self-serve onboarding (SMRTSTATS-350). Theme tokens so
// it reads correctly in both light and dark.
const Reassure = styled.ul`
  list-style: none;
  margin: 16px 0 4px;
  padding: 14px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: ${(p) => p.theme.cardBgAlt};
  border-left: 3px solid ${(p) => p.theme.green};
  border-radius: 8px;
`;

const ReassureItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  font-family: "SatoshiRegular", sans-serif;
  font-size: 13px;
  line-height: 19px;
  color: ${(p) => p.theme.white};
  strong {
    font-family: "SatoshiBold", sans-serif;
  }
`;

const Check = styled.span`
  flex: 0 0 auto;
  color: ${(p) => p.theme.green};
  font-family: "SatoshiBold", sans-serif;
  font-size: 14px;
  line-height: 19px;
`;

const Disclaimer = styled.span`
  font-family: "SatoshiRegular", sans-serif;
  font-size: 12px;
  line-height: 18px;
  color: ${(p) => p.theme.blueish2};
  a {
    font-size: 12px;
    color: ${(p) => p.theme.blueish2};
    text-decoration: underline;
  }
`;
